import { useState, useEffect, useCallback } from 'react'
import { getSellingUnitPayload } from '../utils/orderItemUnits.js'
import { 
  getSalesOrders, 
  getSalesOrderDetail, 
  createSalesOrder, 
  updateSalesOrderStatus, 
  deleteSalesOrder,
  addOrderPayment,
  getOrderPayments,
  createQuickSale,
  generateTransactionNumber,
  validateOrder,
  calculateOrderTotals as apiCalculateOrderTotals
} from '../api/salesService.js'
import { 
  calculateOrderTotals, 
  calculateOrderItemTotals,
  calculatePaymentTotals, 
  calculateChange,
  getPaymentStatus,
  validateOrderData,
  addToCart,
  updateCartItemQuantity,
  removeFromCart,
  clearCart
} from '../utils/salesUtils.js'

/**
 * Custom hook for managing sales orders
 */
export const useSalesOrders = (initialFilters = {}) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  })
  const [filters, setFilters] = useState(initialFilters)

  // Fetch orders with current filters and pagination
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await getSalesOrders({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      })
      
      setOrders(response.data || [])
      setPagination(prev => ({
        ...prev,
        total: response.meta?.total || 0
      }))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.page, pagination.limit])

  // Update filters and reset to first page
  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters)
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  // Change page
  const changePage = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }, [])

  // Change items per page
  const changeLimit = useCallback((newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))
  }, [])

  // Refresh current data
  const refresh = useCallback(() => {
    fetchOrders()
  }, [fetchOrders])

  // Update order status
  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    try {
      await updateSalesOrderStatus(orderId, { status: newStatus })
      // Refresh to get updated data
      await fetchOrders()
      return true
    } catch (err) {
      setError(err.message)
      return false
    }
  }, [fetchOrders])

  // Delete order
  const deleteOrder = useCallback(async (orderId, reason) => {
    try {
      await deleteSalesOrder(orderId, { cancellation_reason: reason?.trim() || '' })
      // Refresh to get updated data
      await fetchOrders()
      return true
    } catch (err) {
      setError(err.message)
      return false
    }
  }, [fetchOrders])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  return {
    orders,
    loading,
    error,
    pagination,
    filters,
    updateFilters,
    changePage,
    changeLimit,
    refresh,
    updateOrderStatus,
    deleteOrder
  }
}

/**
 * Custom hook for managing a single sales order
 */
export const useSalesOrder = (orderId) => {
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchOrder = useCallback(async () => {
    if (!orderId) return

    try {
      setLoading(true)
      setError(null)
      
      const response = await getSalesOrderDetail(orderId)
      setOrder(response.data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [orderId])

  const refresh = useCallback(() => {
    fetchOrder()
  }, [fetchOrder])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  return {
    order,
    loading,
    error,
    refresh
  }
}

const getActiveCartDraft = () => {
  try {
    const raw = localStorage.getItem("cart_pos_active_draft") || localStorage.getItem("pos_active_cart_draft");
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
};

/**
 * Custom hook for managing order creation and cart
 */
export const useOrderCreation = (initialCustomer = null) => {
  const initialDraft = getActiveCartDraft();

  const [cart, setCart] = useState(() => (Array.isArray(initialDraft?.cart) ? initialDraft.cart : []));
  const [customer, setCustomer] = useState(() => (initialDraft?.customer !== undefined ? initialDraft.customer : initialCustomer));
  const [sellerType, setSellerType] = useState(() => (initialDraft?.salesType || { id: null, slug: 'REGULAR', name: 'Regular', jenis: 'Normal', nilai: 0 })); // Store object with id, slug and name
  const [additionalCosts, setAdditionalCosts] = useState(() => Number(initialDraft?.additionalCosts || 0));
  const [discountTotal, setDiscountTotal] = useState(() => Number(initialDraft?.discountTotal || 0));
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState(() => initialDraft?.notes || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-calculate discount or markup from sellerType
  useEffect(() => {
    const subtotal = cart.reduce((sum, item) => sum + calculateOrderItemTotals(item).subtotal, 0)
    let newDiscount = 0
    let newMarkup = 0

    if (sellerType && sellerType.jenis) {
      const { jenis, satuan, nilai } = sellerType
      const numNilai = Number(nilai) || 0

      if (jenis === 'Diskon') {
        newDiscount = satuan === '%' ? (subtotal * numNilai / 100) : numNilai
      } else if (jenis === 'Markup') {
        newMarkup = satuan === '%' ? (subtotal * numNilai / 100) : numNilai
      }
    }

    setDiscountTotal(newDiscount)
    setAdditionalCosts(newMarkup)
  }, [cart, sellerType])

  // Calculate totals based on current cart and adjustments
  const totals = calculateOrderTotals(cart, additionalCosts, discountTotal)
  const paymentTotals = calculatePaymentTotals(payments)
  const changeAmount = calculateChange(paymentTotals.totalPaid, totals.grandTotal)
  const paymentStatus = getPaymentStatus(paymentTotals.totalPaid, totals.grandTotal)

  // Cart management functions
  const addItemToCart = useCallback((item) => {
    setCart(prevCart => addToCart(prevCart, item))
  }, [])

  const updateItemQuantity = useCallback((itemIndex, newQuantity) => {
    setCart(prevCart => updateCartItemQuantity(prevCart, itemIndex, newQuantity))
  }, [])

  const updateItemUnit = useCallback((itemIndex, unit) => {
    setCart(prevCart => {
      if (itemIndex < 0 || itemIndex >= prevCart.length) return prevCart

      const updatedCart = [...prevCart]
      updatedCart[itemIndex] = {
        ...updatedCart[itemIndex],
        unit,
        unit_id: unit?.id || null,
        unit_name: unit?.name || null
      }
      return updatedCart
    })
  }, [])

  const updateItemNotes = useCallback((itemIndex, notes) => {
    setCart(prevCart => {
      if (itemIndex < 0 || itemIndex >= prevCart.length) return prevCart

      const updatedCart = [...prevCart]
      updatedCart[itemIndex] = {
        ...updatedCart[itemIndex],
        notes
      }
      return updatedCart
    })
  }, [])

  const updateItemPrice = useCallback((itemIndex, newPrice, priceObj) => {
    setCart(prevCart => {
      if (itemIndex < 0 || itemIndex >= prevCart.length) return prevCart

      const updatedCart = [...prevCart]
      const item = { ...updatedCart[itemIndex] }
      const p = Number(newPrice)
      item.price = p
      item.unit_price = p
      item.base_price = p
      item.selected_price_id = priceObj ? priceObj.id : null
      item.selected_price_name = priceObj ? priceObj.name : null
      item.total_price = calculateOrderItemTotals(item).subtotal
      updatedCart[itemIndex] = item
      return updatedCart
    })
  }, [])

  const removeItemFromCart = useCallback((itemIndex) => {
    setCart(prevCart => removeFromCart(prevCart, itemIndex))
  }, [])

  const clearOrderCart = useCallback(() => {
    setCart(clearCart())
  }, [])

  const replaceCart = useCallback((items = []) => {
    setCart(Array.isArray(items) ? items : [])
  }, [])

  // Payment management
  const addPayment = useCallback((payment) => {
    setPayments(prevPayments => [...prevPayments, payment])
  }, [])

  const removePayment = useCallback((paymentIndex) => {
    setPayments(prevPayments => {
      const newPayments = [...prevPayments]
      newPayments.splice(paymentIndex, 1)
      return newPayments
    })
  }, [])

  const clearPayments = useCallback(() => {
    setPayments([])
  }, [])

  // Validate current order data
  const validation = validateOrderData({
    customer_id: customer?.id,
    customer_name: customer?.name || '',
    seller_type: sellerType?.name || sellerType || 'Internal',
    order_type_id: sellerType?.id,
    items: cart,
    additional_costs: additionalCosts,
    discount_total: discountTotal,
    payments,
    notes
  })

  // Create order
  const createOrder = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      const orderData = {
        customer_id: customer?.id,
        customer_name: customer?.name || '',
        seller_type: sellerType?.name || sellerType || 'Internal',
        order_type_id: sellerType?.id,
        items: cart.reduce((acc, item) => {
          if (item.item_type === "PRODUCT") {
            const pId = item.product_id || item.id;
            const pVariantId = item.variant_id || item.product_variant_id || null;
            // 1. Base product/variant item
            acc.push({
              item_type: item.item_type,
              product_id: pId,
              product_variant_id: pVariantId,
              product_option_id: null,
              service_id: null,
              quantity: item.quantity || item.qty || 1,
              unit_id: item.unit_id,
              ...getSellingUnitPayload(item),
              unit_price: item.base_price ?? item.unit_price,
              discount_amount: Number(item.discount_amount || 0),
              additional_cost: Number(item.additional_cost || 0),
              notes: item.notes || ''
            });

            // 2. Add each selected option as its own OrderItem
            if (item.selected_options && item.selected_options.length > 0) {
              item.selected_options.forEach(opt => {
                acc.push({
                  item_type: "PRODUCT",
                  product_id: pId,
                  product_variant_id: null,
                  product_option_id: opt.id,
                  service_id: null,
                  quantity: (item.quantity || item.qty || 1) * (opt.qty || 1),
                  unit_id: null,
                  unit_price: opt.adjusted_price || opt.price || opt.sell_price || 0,
                  notes: `Add-on: ${opt.name}`
                });
              });
            }
          } else {
            // Service
            acc.push({
              item_type: item.item_type,
              product_id: null,
              product_variant_id: null,
              product_option_id: null,
              service_id: item.service_id,
              quantity: item.quantity,
              unit_id: item.unit_id,
              unit_price: item.unit_price,
              discount_amount: Number(item.discount_amount || 0),
              additional_cost: Number(item.additional_cost || 0),
              notes: item.notes || ''
            });
          }
          return acc;
        }, []),
        additional_costs: additionalCosts,
        discount_total: discountTotal,
        payments,
        notes
      }

      const response = await createSalesOrder(orderData)
      
      // Clear form after successful creation
      clearOrderCart()
      clearPayments()
      setAdditionalCosts(0)
      setDiscountTotal(0)
      setNotes('')
      
      return response.data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [
    cart, customer, sellerType, additionalCosts, discountTotal, 
    payments, notes, validation, clearOrderCart, clearPayments
  ])

  // Create quick sale (simplified process)
  const createQuickSaleOrder = useCallback(async (cashReceived) => {
    try {
      setLoading(true)
      setError(null)

      const quickSaleData = {
        items: cart.map(item => ({
          item_type: item.item_type,
          product_id: item.product_id,
          product_variant_id: item.product_variant_id,
          product_option_id: item.product_option_id,
          service_id: item.service_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_amount: Number(item.discount_amount || 0),
          additional_cost: Number(item.additional_cost || 0),
          notes: item.notes || ''
        })),
        customer_name: customer?.name || '',
        cash_received: cashReceived
      }

      const response = await createQuickSale(quickSaleData)
      
      // Clear form after successful creation
      clearOrderCart()
      setCustomer(null)
      
      return response.data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [cart, customer, clearOrderCart])

  // Reset entire form
  const resetForm = useCallback(() => {
    clearOrderCart()
    clearPayments()
    setCustomer(null)
    setSellerType({ id: null, slug: 'REGULER', name: 'Reguler' })
    setAdditionalCosts(0)
    setDiscountTotal(0)
    setNotes('')
    setError(null)
  }, [clearOrderCart, clearPayments])

  return {
    // Cart data
    cart,
    customer,
    sellerType,
    additionalCosts,
    discountTotal,
    payments,
    notes,
    
    // Calculated values
    totals,
    paymentTotals,
    changeAmount,
    paymentStatus,
    validation,
    
    // State
    loading,
    error,
    
    // Cart actions
    addItemToCart,
    updateItemQuantity,
    updateItemUnit,
    updateItemNotes,
    updateItemPrice,
    removeItemFromCart,
    clearOrderCart,
    replaceCart,
    
    // Payment actions
    addPayment,
    removePayment,
    clearPayments,
    
    // Form setters
    setCustomer,
    setSellerType,
    setAdditionalCosts,
    setDiscountTotal,
    setNotes,
    
    // Order creation
    createOrder,
    createQuickSaleOrder,
    resetForm
  }
}

/**
 * Custom hook for order payments management
 */
export const useOrderPayments = (orderId) => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchPayments = useCallback(async () => {
    if (!orderId) return

    try {
      setLoading(true)
      setError(null)
      
      const response = await getOrderPayments(orderId)
      setPayments(response.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [orderId])

  const addPayment = useCallback(async (paymentData) => {
    try {
      setLoading(true)
      setError(null)
      
      await addOrderPayment(orderId, paymentData)
      // Refresh payment list
      await fetchPayments()
      return true
    } catch (err) {
      setError(err.message)
      return false
    } finally {
      setLoading(false)
    }
  }, [orderId, fetchPayments])

  useEffect(() => {
    fetchPayments()
  }, [fetchPayments])

  return {
    payments,
    loading,
    error,
    addPayment,
    refresh: fetchPayments
  }
}

/**
 * Custom hook for generating transaction numbers
 */
export const useTransactionNumber = () => {
  const [transactionNumber, setTransactionNumber] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const generate = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await generateTransactionNumber()
      const newNumber = response.data?.transaction_number || ''
      setTransactionNumber(newNumber)
      return newNumber
    } catch (err) {
      setError(err.message)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    transactionNumber,
    loading,
    error,
    generate
  }
}
